/**
 * The rating form's state, kept pure so the rules are testable without a DOM: what the link
 * pre-selects, what a previous submission contributes, and what may be sent.
 */
import {
  FEEDBACK_SUGGESTIONS_MAX,
  type FeedbackContextResponse,
  type FeedbackScore,
  type FeedbackSubmitBody
} from '@portal/contracts'

export interface RatingForm {
  responseTime?: FeedbackScore
  assertiveness?: FeedbackScore
  suggestions: string
}

export const SCORES: readonly FeedbackScore[] = [1, 2, 3, 4, 5]

/** A `?pt=` / `?as=` value from the e-mail's star link: an integer 1–5, anything else ignored. */
export function parseScore(raw: string | null | undefined): FeedbackScore | undefined {
  if (raw == null || !/^[1-5]$/.test(raw.trim())) return undefined
  return Number(raw.trim()) as FeedbackScore
}

/**
 * The form a reader lands on: a previous submission through the same link, overridden by
 * the star they just clicked — the click is the newer intent.
 */
export function initialForm(context: FeedbackContextResponse | undefined, query: URLSearchParams): RatingForm {
  const clickedTime = parseScore(query.get('pt'))
  const clickedAccuracy = parseScore(query.get('as'))
  const responseTime = clickedTime ?? context?.ratings?.responseTime
  const assertiveness = clickedAccuracy ?? context?.ratings?.assertiveness
  return {
    ...(responseTime ? { responseTime } : {}),
    ...(assertiveness ? { assertiveness } : {}),
    suggestions: context?.suggestions ?? ''
  }
}

/**
 * The body to send, or null when the form holds nothing (the service refuses an empty rating).
 *
 * The form is what the reader wants stored, so beside a rating the suggestion is always sent —
 * empty when they emptied it, which is how fr-docs clears the previous one. With no rating, an
 * empty suggestion cannot be sent: fr-docs refuses a submission with nothing in it.
 */
export function submitBody(token: string, form: RatingForm): FeedbackSubmitBody | null {
  const suggestions = form.suggestions.trim().slice(0, FEEDBACK_SUGGESTIONS_MAX)
  const rated = form.responseTime !== undefined || form.assertiveness !== undefined
  if (!rated && suggestions.length === 0) return null
  return {
    token,
    ...(form.responseTime !== undefined ? { responseTime: form.responseTime } : {}),
    ...(form.assertiveness !== undefined ? { assertiveness: form.assertiveness } : {}),
    ...(rated || suggestions ? { suggestions } : {})
  }
}

/** The page opens in the answer's language unless the reader already chose one. */
export function pageLocale(answerLocale: string, stored: 'en' | 'pt-BR' | null): 'en' | 'pt-BR' | null {
  if (stored) return null
  return answerLocale.toLowerCase().startsWith('en') ? 'en' : 'pt-BR'
}

/**
 * The locale change the rating page makes for the answer's language: never persisted, so a
 * reader who follows a link to an answer in another language keeps their own choice on every
 * other page.
 */
export function pageLocaleChange(
  answerLocale: string,
  stored: 'en' | 'pt-BR' | null,
  current: 'en' | 'pt-BR'
): { locale: 'en' | 'pt-BR'; options: { persist: false } } | null {
  const pick = pageLocale(answerLocale, stored)
  return pick && pick !== current ? { locale: pick, options: { persist: false } } : null
}
